import RequiredAsterisk from 'components/RequiredAsterisk';
import { InputLabel, Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useField } from 'formik';
import { useEffect, useRef, useState } from 'react';
import AsyncSelectSearchFormFieldProps from './dto';
import { READ_ONLY_FIELD_STYLES } from 'utils/field-styles';

const AsyncSelectSearchFormField = <T,>({
    label,
    isRequired = false,
    multiple,
    initialValues,
    fetchData,
    disabled = false,
    getOptionLabel,
    isOptionEqualToValue,
    noOptionsText,
    filterOptions,
    onChangeCallback,
    ...props
}: AsyncSelectSearchFormFieldProps<T>) => {
    const [field, meta, helpers] = useField(props);
    const [options, setOptions] = useState<T[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [value, setValue] = useState<T | Array<T> | null>(initialValues ?? (multiple ? [] : null));
    const [inputValue, setInputValue] = useState('');

    const hasError = Boolean(meta.touched && meta.error);

    const interval = useRef<any>();

    useEffect(() => {
        setLoading(true);

        interval.current = setTimeout(
            () =>
                fetchData(inputValue)
                    .then((options) => setOptions(options))
                    .finally(() => setLoading(false)),
            500,
        );

        return () => {
            clearTimeout(interval.current);
        };
    }, [inputValue, fetchData]);

    return (
        <>
            <InputLabel htmlFor={props.name}>
                {label} {isRequired && <RequiredAsterisk />}
            </InputLabel>
            <Autocomplete
                id={props.name}
                options={options}
                popupIcon={loading ? <CircularProgress size={20} /> : undefined}
                loading={loading}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                {...props}
                {...field}
                disabled={disabled}
                multiple={multiple}
                value={value}
                onChange={(_, option) => {
                    setValue(option);
                    helpers.setValue(option, true);
                    onChangeCallback?.(option);
                }}
                onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                renderInput={(params) => (
                    <TextField
                        error={hasError}
                        helperText={hasError ? meta.error : ''}
                        {...params}
                        InputProps={{
                            ...params.InputProps,
                            sx: disabled ? READ_ONLY_FIELD_STYLES : undefined,
                        }}
                    />
                )}
                filterOptions={filterOptions}
                noOptionsText={noOptionsText}
                sx={disabled ? READ_ONLY_FIELD_STYLES : undefined}
            />
        </>
    );
};

export default AsyncSelectSearchFormField;
